import { useState, type FormEvent } from "react";
import { ArrowRight, CheckCircle2, Loader2, Mail, TriangleAlert } from "lucide-react";
import { useI18n } from "../i18n";
import { cn } from "../lib/utils";
import { buttonVariants } from "./ui/button";
import { Field, Input, Textarea } from "./ui/input";
import { Reveal } from "./reveal";

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Status = "parado" | "enviando" | "ok" | "erro" | "invalido";

/** CTA final — texto à esquerda, formulário em papel branco à direita.
    POSTa em /api/contact (API Java). */
export function Contact() {
  const { t } = useI18n();
  const [status, setStatus] = useState<Status>("parado");

  async function enviar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const dados = {
      nome: form.nome.value.trim(),
      email: form.email.value.trim(),
      empresa: form.empresa.value.trim(),
      mensagem: form.mensagem.value.trim(),
    };

    if (!dados.nome || !dados.mensagem || !RE_EMAIL.test(dados.email)) {
      setStatus("invalido");
      return;
    }

    setStatus("enviando");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dados),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      form.reset();
      setStatus("ok");
    } catch {
      setStatus("erro");
    }
  }

  const mensagem =
    status === "ok"
      ? t("cta.form.ok")
      : status === "erro"
        ? t("cta.form.erro")
        : status === "invalido"
          ? t("cta.form.invalido")
          : "";

  return (
    <section id="contato" className="relative scroll-mt-24 overflow-hidden py-24 sm:py-32">
      <div aria-hidden className="bg-dots bg-dots-fade absolute inset-0" />

      <div className="relative mx-auto max-w-6xl px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
          {/* texto */}
          <Reveal>
            <div className="flex flex-col items-start gap-5 lg:sticky lg:top-28">
              <p className="flex items-center gap-2.5 font-mono text-[11px] font-medium tracking-[0.16em] uppercase">
                <span className="text-subtle">08</span>
                <span aria-hidden className="h-px w-6 bg-line" />
                <span className="text-accent">{t("cta.rotulo")}</span>
              </p>
              <h2 className="font-display text-[clamp(1.9rem,4.5vw,3rem)] leading-[1.08] font-semibold tracking-[-0.02em] text-balance">
                {t("cta.titulo")}
              </h2>
              <p className="max-w-md text-base leading-relaxed text-muted-foreground text-pretty">
                {t("cta.desc")}
              </p>
              <a
                href="mailto:contact@vortolabs.com"
                className="mt-2 inline-flex items-center gap-2 border-b border-foreground/20 pb-0.5 text-sm font-medium text-foreground transition-colors hover:border-accent hover:text-accent"
              >
                <Mail className="size-4" />
                contact@vortolabs.com
              </a>
            </div>
          </Reveal>

          {/* formulário */}
          <Reveal delay={0.1}>
            <form
              onSubmit={enviar}
              noValidate
              className="card-paper rounded-xl p-6 sm:p-8"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("cta.form.nome")}>
                  <Input name="nome" required autoComplete="name" maxLength={120} placeholder={t("cta.form.nomePh")} />
                </Field>
                <Field label={t("cta.form.email")}>
                  <Input name="email" type="email" required autoComplete="email" maxLength={180} placeholder={t("cta.form.emailPh")} />
                </Field>
              </div>
              <div className="mt-5">
                <Field label={t("cta.form.empresa")}>
                  <Input name="empresa" autoComplete="organization" maxLength={120} placeholder={t("cta.form.empresaPh")} />
                </Field>
              </div>
              <div className="mt-5">
                <Field label={t("cta.form.msg")}>
                  <Textarea name="mensagem" rows={4} required maxLength={2000} placeholder={t("cta.form.msgPh")} />
                </Field>
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "enviando"}
                  className={cn(buttonVariants({ variant: "primary", size: "lg" }), "group w-full sm:w-auto")}
                >
                  {status === "enviando" ? (
                    <>
                      {t("cta.form.enviando")}
                      <Loader2 className="animate-spin" />
                    </>
                  ) : (
                    <>
                      {t("cta.form.enviar")}
                      <ArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>

                {/* retorno do envio, no mesmo lugar sempre */}
                <p
                  role="status"
                  aria-live="polite"
                  className={cn(
                    "flex items-center gap-2 text-sm",
                    status === "ok" ? "text-accent" : "text-red-600",
                    mensagem ? "opacity-100" : "opacity-0",
                  )}
                >
                  {status === "ok" && <CheckCircle2 className="size-4" />}
                  {(status === "erro" || status === "invalido") && <TriangleAlert className="size-4" />}
                  {mensagem || " "}
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
